import scrollIntoView from 'scroll-into-view-if-needed';
import { cloneDeep, isEmpty, isPlainObject } from 'lodash';
import moment from 'moment-timezone';
import { Store } from 'vuex';
import { Site } from '@/interfaces/common';

import { SITE } from '@/aquassist-shared/constants/types/node';

import type { NodeTree } from '@/interfaces/site-tree.types';
import csvDownload from 'json-to-csv-export';
import type { RawSeriesItem, FormattedPoint, SeriesInfo } from '@/interfaces/graphic';
import { getStore } from '@/utilities/storeAccess';

export function saveFileOnDisk(blob: Blob, fileName: string): void {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', fileName);
    link.click();
}

export const getLocalizedText = (
    content?: { [key: string]: string } | null,
    locale?: string,
    fallbackLang?: string,
): string => {
    if (!content) return '';
    if (locale && content[locale] && content[locale].length) {
        return content[locale];
    }
    if (fallbackLang && content[fallbackLang] && content[fallbackLang].length) {
        return content[fallbackLang];
    }
    const first = Object.values(content).find((v) => v && v.length);
    return first || '';
};

export const getLocaleLongDateFormat = (
    serviceProviderDateFormat?: string | null,
    localUserDateFormat?: string | null,
    defaultFormat = 'YYYY-MM-DD',
): string => {
    const dateFormat = serviceProviderDateFormat || localUserDateFormat;
    if (dateFormat) {
        const localeData = moment.localeData(dateFormat);
        return localeData.longDateFormat('L');
    }
    return defaultFormat;
};

export const removeEmptyNullOrUndefined = <T extends Record<string, unknown>>(
    object: T,
    checkEmptyString = false,
): Partial<T> => {
    const tmp = cloneDeep(object);

    Object.keys(tmp).forEach((key) => {
        if (checkEmptyString) {
            if (tmp[key] === '' || tmp[key] === null || tmp[key] === undefined) {
                delete tmp[key];
            }
        } else if (tmp[key] === null || tmp[key] === undefined) {
            delete tmp[key];
        }
        if (typeof tmp[key] === 'object' && isEmpty(tmp[key])) {
            delete tmp[key];
        }
    });

    return tmp;
};

export async function watchDomForMountedElement(
    selector: string,
    timeout = 3000,
): Promise<Element> {
    return new Promise((resolve, reject) => {
        const start = Date.now();
        const check = () => {
            const el = document.querySelector(selector);
            if (el) return resolve(el);
            if (Date.now() - start > timeout) return reject(new Error('Element not found'));
            requestAnimationFrame(check);
        };
        check();
    });
}

export async function scrollToId(id: string, offset = 2, idSuffix = 'id_'): Promise<void> {
    const selector = `#${idSuffix}${id}`;
    await watchDomForMountedElement(selector);
    const el = document.getElementById(`${idSuffix}${id}`);
    if (el) {
        scrollIntoView(el, {
            behavior: 'smooth',
            block: 'center',
            scrollMode: 'if-needed',
        });
        el.classList.add('highlight');
        if (offset) {
            // Optionally handle offset if needed
        }
    }
}

/**
 *
 * @param value the date to format
 * @param format the format to use
 * @param store the store to use
 * @param localeOverride the locale we want to use instead of team/site
 * @returns the formatted date
 */
export function formatDate(
    value: string,
    format = 'L',
    store?: Store<unknown>,
    localeOverride?: string,
    userTimezone?: boolean,
): string {
    if (!value) return '';

    if (userTimezone) {
        const tz = moment.tz.guess();
        const locale = localeOverride || 'en';
        return moment(String(value)).tz(tz).locale(locale).format(format);
    }
    const activeStore = store ?? getStore();

    // If no store is available, just format with default locale
    if (!activeStore) {
        return moment(String(value)).format(format);
    }

    const lang = activeStore.getters.lang as string | undefined;
    const teamDateFormat = activeStore.getters.teamDateFormat;
    const site = activeStore.getters.site as Site | undefined;
    const tz = site?.tz ? site.tz : moment.tz.guess();
    const mom = typeof value === 'object' ? moment(value).tz(tz) : moment(String(value)).tz(tz);

    // priority is override -> sp date format -> user lang
    const locale = localeOverride || teamDateFormat || lang;
    return mom.locale(locale).format(format);
}

export const getTomorrowDate = () => moment().add(1, 'days').format();

// Helper function to find a node by ref in the tree
export const findParentNode = (
    tree: NodeTree[],
    parentRef: string,
    targetNode?: NodeTree,
): NodeTree | undefined => {
    if (!parentRef && !targetNode) return undefined;

    if (parentRef) {
        const findNode = (nodes: NodeTree[], ref: string): NodeTree | undefined => {
            for (const node of nodes) {
                if (node.ref === ref) return node;
                if (node.children && node.children.length > 0) {
                    const found = findNode(node.children, ref);
                    if (found) return found;
                }
            }
            return undefined;
        };
        return findNode(tree, parentRef);
    } else if (targetNode) {
        const findNode = (
            nodes: NodeTree[],
            ref: string,
            upperNode?: NodeTree,
        ): NodeTree | undefined => {
            for (const node of nodes) {
                if (node.nodeType === SITE && node.children?.length) {
                    return findNode(node.children, ref, node);
                } else if (node.ref === ref) return upperNode;
                if (node.children && node.children.length > 0) {
                    const found = findNode(node.children, ref, node);
                    if (found) return found;
                }
            }
            return undefined;
        };
        return findNode(tree, targetNode.ref);
    } else {
        return undefined;
    }
};

// Helper function to find a node by ref in a TreeNode array
export const findNode = (tree: NodeTree[] = [], ref: string): NodeTree | undefined => {
    const response = tree.map((node) => (node.ref === ref ? node : findNode(node.children, ref)));
    return response.find((a) => a || null);
};

// Define an interface for the rawHtml structure
interface RawHtmlData {
    prop: string;
    rawValue: unknown;
}

// Define an interface for the row item
interface DataRow {
    rawHtml?: RawHtmlData;
    [key: string]: unknown;
}

export const exportAsCSV = (data: unknown[], headers: unknown[]) => {
    // Create a deep to copy to manipulate
    const fields = cloneDeep(headers);

    // Remove ID if already exists
    const idIndex = fields.indexOf('id');
    if (idIndex !== -1) {
        fields.splice(idIndex, 1);
    }

    fields.unshift('id');

    const dataCsv = data
        .map((row) => {
            const item = cloneDeep(row) as DataRow;

            // We get the value from the rawHtml object
            if (item.rawHtml) {
                item[item.rawHtml.prop] = item.rawHtml.rawValue;
            }

            return (
                Object.keys(item)
                    // We return only what the user is seeing
                    .filter((key) => fields.includes(key))
                    // For specific columns, we can have an Object as a value,
                    // so we try to access the key 'value', of simply "dump" the object as string
                    .map((key) => {
                        let value;

                        if (!isPlainObject(item[key])) {
                            value = item[key];
                        } else {
                            value =
                                (item[key] as Record<string, unknown>)['value'] ??
                                JSON.stringify(item[key]);
                        }

                        return { [key]: value };
                    })
                    // We return back the object with all props
                    .reduce((res, o) => Object.assign(res, o), {})
            );
        })
        // Add an incremental id to the object
        .map((item, index) => ({
            id: index.toString(),
            ...item,
        }));
    // Download the csv to the download location of the browser
    return csvDownload({ data: dataCsv });
};

export const convertToUtcMidnight = (dateStr: string): Date => {
    const datePart = dateStr.includes('T') ? dateStr.split('T')[0] : dateStr;
    const [year, month, day] = datePart.split('-').map(Number);
    return new Date(Date.UTC(year, month - 1, day, 0, 0, 0, 0));
};

export const convertToUtcEndOfDay = (dateStr: string): Date => {
    const datePart = dateStr.includes('T') ? dateStr.split('T')[0] : dateStr;
    const [year, month, day] = datePart.split('-').map(Number);
    return new Date(Date.UTC(year, month - 1, day, 23, 59, 59, 999));
};

/**
 * Resolves the base URL the CRM submodule issues its requests against.
 *
 * It must be ABSOLUTE. CRM shares the shell's authenticated axios instance, whose `baseURL` is
 * `VUE_APP_API_URL` (ending in `/v1`), and axios only bypasses `baseURL` for absolute URLs — a
 * relative `/crm/v1` silently becomes `.../v1/crm/v1/...`. Deployed environments serve both
 * services from one ALB host split by path prefix, so the CRM origin is derived from the
 * management API URL; `override` (VUE_APP_CRM_API_URL) wins when set, which is how local dev
 * reaches :3002 and how CRM could later move to its own host.
 */
export const resolveCrmBaseUrl = (apiUrl?: string, override?: string): string => {
    if (override) {
        return override;
    }
    return `${apiUrl ?? ''}`.replace(/\/v1\/?$/, '/crm/v1');
};

export const regexes: Record<string, RegExp> = {
    clientSite: /^\/clients\/[^/]+\/sites\/[^/]+/,
    client: /^\/clients\/[^/]+/,
    fieldService: /^\/?services?/,
    administration: /^\/administration/,
    laboratory: /^\/laboratory/,
    crm: /^\/crm/,
};

function escapeCsvValue(value: string): string {
    if (typeof value === 'string' && (value.includes(',') || value.includes('"'))) {
        return `"${value.replace(/"/g, '""')}"`;
    }
    return value;
}

export const exportSimpleCSV = <T extends RawSeriesItem>(
    header: string[],
    data: T[],
    title: string,
    type: string,
    detail?: 'sites' | 'users',
) => {
    const rows = [header];
    if (type === 'billingRegister') {
        data.forEach((_series) => {
            const series = _series as unknown as Record<string, string>;
            if (detail === 'sites') {
                rows.push([
                    escapeCsvValue(series.serviceProvider),
                    escapeCsvValue(series.clientNumber),
                    escapeCsvValue(series.clientName),
                    escapeCsvValue(series.siteNumber),
                    escapeCsvValue(series.siteName),
                    escapeCsvValue(series.linkedUsers),
                    escapeCsvValue(series.linkedDevices),
                ]);
            }
            if (detail === 'users') {
                rows.push([
                    escapeCsvValue(series.serviceProvider),
                    escapeCsvValue(series.firstName),
                    escapeCsvValue(series.lastName),
                    escapeCsvValue(series.email),
                    escapeCsvValue(series.phone),
                    escapeCsvValue(series.role),
                    escapeCsvValue(series.memberDate),
                ]);
            }
        });
    }
    const csvContent = rows.map((row) => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', title);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
};

export const exportGraphDataCSV = (series: SeriesInfo[], filename = 'series_data.csv') => {
    function getMeasuredAt(point: FormattedPoint): string {
        const p = point as unknown as Record<string, unknown>;
        if (typeof p.measuredAt === 'string') return p.measuredAt;
        if (typeof p.measuredAt === 'number') return new Date(p.measuredAt).toISOString();
        if (typeof p.timestamp === 'string') return p.timestamp;
        if (typeof p.timestamp === 'number') return new Date(p.timestamp).toISOString();
        if (point.date) return point.date.toISOString();
        return '';
    }

    function getPointUnit(point: FormattedPoint, seriesUnit: string): string {
        const p = point as unknown as { metadata?: { unit?: string }; unit?: string };
        return p.metadata?.unit ?? p.unit ?? seriesUnit;
    }

    const header = [
        'Series Name',
        'Network Name',
        'Component Name',
        'Analysis Name',
        'Measured At',
        'Value',
        'Unit',
    ];
    const rows: string[][] = [header];
    series.forEach((s) => {
        const seriesName = s.componentName
            ? `${s.analysisName} - ${s.componentName}`
            : s.analysisName;
        const networkName = s.seriesInfo.network?.name || '';
        const componentName = s.componentName || '';
        const analysisName = s.analysisName || '';
        const seriesUnit = s.unit || '';
        (s.seriesInfo.data || []).forEach((rawPoint) => {
            const point = rawPoint as unknown as FormattedPoint;
            const measuredAt = formatDate(getMeasuredAt(point), 'L LT');
            const value = point.value != null ? String(point.value) : '';
            const unit = getPointUnit(point, seriesUnit);
            rows.push([
                escapeCsvValue(seriesName),
                escapeCsvValue(networkName),
                escapeCsvValue(componentName),
                escapeCsvValue(analysisName),
                escapeCsvValue(measuredAt),
                escapeCsvValue(value),
                escapeCsvValue(unit),
            ]);
        });
    });
    const csvContent = rows.map((row) => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
};
